import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useRef } from "react";
import MapTileLayers from "@/components/Map/MapTileLayers";
import { MapContainer, Marker, useMap, useMapEvents } from "react-leaflet";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: "/leaflet/images/marker-icon-2x.png",
  iconUrl: "/leaflet/images/marker-icon.png",
  shadowUrl: "/leaflet/images/marker-shadow.png",
});

interface LocationPickerMapProps {
  latitude: string;
  longitude: string;
  onLocationChange: (lat: number, lng: number) => void;
  height?: string;
  zoomLevel?: number;
  animateZoom?: boolean;
}

const MapController = ({
  latitude,
  longitude,
  zoomLevel = 15,
  animateZoom = true,
}: {
  latitude: string;
  longitude: string;
  zoomLevel?: number;
  animateZoom?: boolean;
}) => {
  const map = useMap();

  useEffect(() => {
    if (map && latitude && longitude) {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);

      if (!isNaN(lat) && !isNaN(lng)) {
        const timeoutId = setTimeout(() => {
          try {
            const newLatLng = L.latLng(lat, lng);
            map.setView(newLatLng, zoomLevel, {
              animate: animateZoom,
              duration: 1,
            });
          } catch (err) {
            console.error("Erro ao ajustar o mapa:", err);
          }
        }, 100);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [latitude, longitude, map, zoomLevel, animateZoom]);

  return null;
};

const LocationPickerMap = ({
  latitude,
  longitude,
  onLocationChange,
  height = "400px",
  zoomLevel = 15,
  animateZoom = true,
}: LocationPickerMapProps) => {
  const markerRef = useRef<L.Marker | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const initialLat = parseFloat(latitude) || -23.5505;
  const initialLng = parseFloat(longitude) || -46.63333;

  const MapEvents = () => {
    useMapEvents({
      click: (e) => {
        onLocationChange(e.latlng.lat, e.latlng.lng);
      },
      load: (e) => {
        mapRef.current = e.target;
      },
    });
    return null;
  };

  useEffect(() => {
    if (markerRef.current && latitude && longitude) {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);

      if (!isNaN(lat) && !isNaN(lng)) {
        const newLatLng = new L.LatLng(lat, lng);
        markerRef.current.setLatLng(newLatLng);
      }
    }
  }, [latitude, longitude]);

  return (
    <MapContainer
      center={[initialLat, initialLng]}
      zoom={latitude && longitude ? zoomLevel : 5}
      scrollWheelZoom={true}
      style={{ height: height, width: "100%", borderRadius: "0.5rem" }}
    >
      <MapTileLayers position="topright" showMapbox={false} />

      <MapController
        latitude={latitude}
        longitude={longitude}
        zoomLevel={zoomLevel}
        animateZoom={animateZoom}
      />

      {latitude && longitude && (
        <Marker
          position={[parseFloat(latitude), parseFloat(longitude)]}
          draggable={true}
          eventHandlers={{
            dragend: (e) => {
              const marker = e.target;
              const position = marker.getLatLng();
              onLocationChange(position.lat, position.lng);
            },
          }}
          ref={markerRef}
        />
      )}
      <MapEvents />
    </MapContainer>
  );
};

export default LocationPickerMap;
